/**
 * Color palette for series charts - matches legacy SERIES_COLOR_PALETTE
 */
export const SERIES_COLOR_PALETTE = [
  'var(--vis-azure-50)',
  'var(--vis-yellow-50)',
  'var(--vis-mint-50)',
  'var(--vis-salmon-50)',
  'var(--vis-slateblue-50)',
  'var(--vis-olive-50)',
  'var(--vis-pink-50)',
  'var(--vis-turquoise-50)',
  'var(--vis-orange-50)',
  'var(--vis-blue-50)',
];

/**
 * Lighter color palette for previous period comparison series.
 * Uses -70 shades to match Ember's buildColorsForTimeComparison().
 */
export const COMPARISON_COLOR_PALETTE = [
  'var(--vis-azure-70)',
  'var(--vis-yellow-70)',
  'var(--vis-mint-70)',
  'var(--vis-salmon-70)',
  'var(--vis-slateblue-70)',
  'var(--vis-olive-70)',
  'var(--vis-pink-70)',
  'var(--vis-turquoise-70)',
  'var(--vis-orange-70)',
  'var(--vis-blue-70)',
];

/**
 * Default blue gradient for heatmaps (higher values = lighter blue)
 */
export const DEFAULT_HEATMAP_COLORS: [number, string][] = [
  [0, 'var(--vis-blue-70)'],
  [0.14, 'var(--vis-blue-70)'],
  [0.28, 'var(--vis-blue-60)'],
  [0.5, 'var(--vis-blue-50)'],
  [0.68, 'var(--vis-blue-40)'],
  [0.82, 'var(--vis-blue-30)'],
  [1, 'var(--vis-blue-20)'],
];

/**
 * CSAT-specific gradient (red → yellow → green for satisfaction scores)
 */
export const CSAT_COLOR_MAPPINGS: [number, string][] = [
  [0, 'var(--vis-salmon-40)'],
  [0.25, 'var(--vis-yellow-50)'],
  [0.5, 'var(--vis-yellow-70)'],
  [0.75, 'var(--vis-green-60)'],
  [1, 'var(--vis-green-40)'],
];

/**
 * Fixed color mappings for specific dimension values.
 * Ported from Ember's COLOR_MAPPINGS in reporting/flexible/constants.js.
 */
export const COLOR_MAPPINGS: Partial<Record<string, Record<string, string>>> = {
  channel_type: {
    'Android App': 'var(--vis-salmon-50)',
    Desktop: 'var(--vis-azure-50)',
    Email: 'var(--vis-yellow-50)',
    Instagram: 'var(--vis-slateblue-50)',
    'iOS App': 'var(--vis-mint-50)',
    'Phone call': 'var(--vis-olive-50)',
    'Phone switch': 'var(--vis-blue-50)',
    SMS: 'var(--vis-orange-50)',
    Twitter: 'var(--vis-pink-50)',
    WhatsApp: 'var(--vis-turquoise-50)',
  },
  conversation_state: {
    opened: 'var(--vis-pink-50)',
    snoozed: 'var(--vis-turquoise-50)',
    closed: 'var(--vis-slateblue-50)',
  },
  'user.location.continent_code': {
    AF: 'var(--vis-orange-50)',
    AN: 'var(--vis-blue-50)',
    AS: 'var(--vis-mint-50)',
    EU: 'var(--vis-azure-50)',
    NA: 'var(--vis-salmon-50)',
    OC: 'var(--vis-yellow-50)',
    SA: 'var(--vis-turquoise-50)',
  },
  'conversation_rating.rating_index': {
    5: 'var(--vis-green-40)',
    4: 'var(--vis-green-60)',
    3: 'var(--vis-yellow-70)',
    2: 'var(--vis-yellow-50)',
    1: 'var(--vis-salmon-40)',
  },
  'workflow_conversation_rating.rating_index': {
    5: 'var(--vis-green-40)',
    4: 'var(--vis-green-60)',
    3: 'var(--vis-yellow-70)',
    2: 'var(--vis-yellow-50)',
    1: 'var(--vis-salmon-40)',
  },
  'bot_conversation_rating.rating_index': {
    5: 'var(--vis-green-40)',
    4: 'var(--vis-green-60)',
    3: 'var(--vis-yellow-70)',
    2: 'var(--vis-yellow-50)',
    1: 'var(--vis-salmon-40)',
  },
  rating_index: {
    5: 'var(--vis-green-40)',
    4: 'var(--vis-green-60)',
    3: 'var(--vis-yellow-70)',
    2: 'var(--vis-yellow-50)',
    1: 'var(--vis-salmon-40)',
  },
  'fin.resolution_state': {
    confirmed_resolved: 'var(--vis-mint-40)',
    assumed_resolved: 'var(--vis-mint-80)',
  },
  'fin.participated': {
    0: 'var(--vis-azure-70)',
    1: 'var(--vis-azure-40)',
  },
};

/**
 * Metrics that use the CSAT color scheme
 */
export const CSAT_METRICS = new Set([
  'conversation_rating.rating_index',
  'workflow_conversation_rating.rating_index',
  'bot_conversation_rating.rating_index',
  'rating_index',
  'conversation_rating.human.csat',
  'conversation_rating.human.csat.first_user_conversation_part_created_at',
  'conversation_rating.workflows.csat',
  'conversation_rating.ai_agent.csat',
  'conversation_rating.any_agent.csat',
]);

/** Minimum pixel height for bar/column data points to ensure small values remain visible */
export const MIN_POINT_LENGTH = 4;

export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_HOUR = 3600;
export const SECONDS_PER_DAY = 86400;
export const SECONDS_PER_MONTH = 86400 * 31; // Matches Ember's SECONDS_IN.month
export const SECONDS_PER_YEAR = 86400 * 365; // Matches Ember's SECONDS_IN.year

/**
 * Smart tick intervals for duration metrics (in seconds).
 * These provide "nice numbers" for y-axis labels instead of arbitrary values.
 * Matches Embercom's SecondAxis intervals.
 */
export const DURATION_TICK_INTERVALS = [
  1, // 1 second
  2, // 2 seconds
  5, // 5 seconds
  10, // 10 seconds
  30, // 30 seconds
  60, // 1 minute
  60 * 5, // 5 minutes
  60 * 15, // 15 minutes
  60 * 30, // 30 minutes
  60 * 45, // 45 minutes
  60 * 60, // 1 hour
  60 * 90, // 1.5 hours
  60 * 60 * 3, // 3 hours
  60 * 60 * 5, // 5 hours
  60 * 60 * 10, // 10 hours
  60 * 60 * 24, // 1 day
  60 * 60 * 24 * 2, // 2 days
  60 * 60 * 24 * 5, // 5 days
] as const;

/**
 * Preferred number of ticks for duration y-axis (min/max range)
 */
export const DURATION_TICK_COUNT = {
  MIN: 2,
  MAX: 5,
} as const;

export const VALUE_TICK_INTERVALS = [
  1, 2, 4, 5, 8, 10, 50, 100, 500, 1000, 5000, 10000, 50000, 100000, 500000, 1000000, 5000000,
  10000000,
] as const;
